/**
 * Team ID Hook
 * Provides team ID for multi-tenant data access
 *
 * Now integrated with TeamContext for proper team membership
 */

import { useActiveTeamId } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

/**
 * Get the current active team ID
 *
 * @returns Team ID for data scoping, or null if not yet loaded
 *
 * @example
 * const teamId = useTeamId();
 * if (!teamId) return <Loading />;
 * const vehiclesRef = collection(db, 'teams', teamId, 'vehicles');
 *
 * @deprecated Prefer useActiveTeamId() from TeamContext for nullable return,
 * or useRequiredTeamId() for guaranteed non-null (throws if missing).
 */
export function useTeamId(): string | null {
  const activeTeamId = useActiveTeamId();

  if (!activeTeamId) {
    // Return null instead of fallback to prevent permission-denied errors
    // from Firestore listeners starting with wrong teamId
    return null;
  }

  return activeTeamId;
}

/**
 * Get team ID and user ID for audit trails
 *
 * @returns Object with teamId (nullable) and userId
 *
 * @example
 * const { teamId, userId } = useTeamContext();
 * if (!teamId) return <Loading />;
 * await createVehicle({ teamId, createdBy: userId, ... });
 */
export function useTeamContext() {
  const teamId = useTeamId();
  const { user } = useAuth();

  const userId = user?.uid || 'anonymous';

  return { teamId, userId };
}
